import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBrain,
  faCheckCircle,
  faClock,
  faExclamationTriangle,
  faFlag,
  faLightbulb,
  faListCheck,
  faScaleBalanced,
  faTriangleExclamation,
} from "@fortawesome/free-solid-svg-icons";
import Disclosure from "./Disclosure";
import {
  BLOOM_NAMES,
  bandFor,
  bloomNumber,
  criteriaSummary,
  criterionLabel,
  parseScore,
  scoreText,
} from "../config/rubric";
import "./QpEvaluation.css";

// The evaluation of one question paper, in the order the questions get asked:
// what is this paper, is it any good, what is wrong with it, then the rubric it
// was judged against, then the evidence.
//
// Everything here is read from what the analysis stored. Where a figure is
// genuinely absent this says so and says why, rather than rendering a zero that
// would read as a measurement - the difference matters most for the two the 0-4
// rubric deliberately does not produce per question (difficulty and time), and
// for coverage, which cannot be measured at all without a course handout.

// ---------------------------------------------------------------- small pieces

// `textual` says the value is a word, not a figure. The two cannot share a type
// size: an assessment like "adequate" set at the numeral size reads louder than
// every real measurement beside it.
const Tile = ({ icon, label, value, unit, note, band, textual }) => (
  <div className={`qpe-tile${band ? ` is-${band}` : ""}${textual ? " is-textual" : ""}`}>
    <span className="qpe-tile-label">
      {icon && <FontAwesomeIcon icon={icon} />} {label}
    </span>
    <span className="qpe-tile-value">
      {value}
      {unit && <small>{unit}</small>}
    </span>
    {note && <span className="qpe-tile-note">{note}</span>}
  </div>
);

const Chips = ({ counts, title }) => {
  const entries = Object.entries(counts || {}).filter(([, n]) => Number(n) > 0);
  if (entries.length === 0) return null;
  // Commonest first: a distribution is read for its shape, and alphabetical
  // order hides the shape.
  entries.sort((a, b) => Number(b[1]) - Number(a[1]));
  return (
    <div className="qpe-chip-group">
      <h4>{title}</h4>
      <div className="qpe-chips">
        {entries.map(([name, count]) => (
          <span className="qpe-chip" key={name}>
            {name}
            {Number(count) > 1 && <b>&times;{count}</b>}
          </span>
        ))}
      </div>
    </div>
  );
};

// ------------------------------------------------------- derived, not invented

// Mean rubric score per criterion across every analysed question. Derived here
// rather than read from the analysis: nothing in analysis_results stores a
// per-criterion aggregate, but every question stores its own rubric verdicts, so
// this is a real measurement of stored data rather than an invented field.
export const aggregateRubric = (questions) => {
  const totals = new Map();
  (questions || []).forEach((question) => {
    Object.entries(question.rubric_assessment || {}).forEach(([name, verdict]) => {
      // A skipped criterion contributes to no mean: averaging it in as a zero
      // would report a paper as weak on the one thing nobody measured.
      if (verdict?.skipped) return;
      const score = parseScore(verdict);
      if (!score) return;
      const entry = totals.get(name)
        || { name, label: verdict?.name || null, sum: 0, count: 0, max: score.max };
      entry.sum += score.value;
      entry.count += 1;
      entry.max = Math.max(entry.max, score.max);
      totals.set(name, entry);
    });
  });
  // Insertion order, which is the order the rubric defines its criteria in.
  return Array.from(totals.values()).map((entry) => ({
    name: entry.name,
    label: entry.label,
    max: entry.max,
    mean: entry.sum / entry.count,
    ratio: entry.sum / entry.count / entry.max,
    count: entry.count,
  }));
};

// Two overview tiles have no per-question figure under the 0-4 rubric, because
// it judges difficulty and workload for the paper rather than question by
// question. These read that verdict so the tile shows an assessment instead of a
// blank.
const paperCriterion = (criteria, key) => (criteria || {})[key] || null;

const paperCriterionText = (criteria, key) => {
  const verdict = paperCriterion(criteria, key);
  if (!verdict) return "not measured";
  if (verdict.skipped) return "not assessed";
  const score = parseScore(verdict);
  if (!score) return "unscored";
  // The rubric's own word for the level - "good", "adequate", "strong" - which
  // says more at a glance than 3/4 does.
  return verdict.label || `${score.value}/${score.max}`;
};

const paperCriterionBand = (criteria, key) => {
  const verdict = paperCriterion(criteria, key);
  if (!verdict || verdict.skipped) return "absent";
  const score = parseScore(verdict);
  return score ? bandFor(score.ratio) : "absent";
};

const paperCriterionNote = (criteria, key, fallback) => {
  const verdict = paperCriterion(criteria, key);
  if (!verdict) return fallback;
  if (verdict.skipped) return verdict.reason || fallback;
  const score = parseScore(verdict);
  const prefix = score
    ? `${verdict.name || criterionLabel(key)} ${score.value}/${score.max}`
    : verdict.name || criterionLabel(key);
  return verdict.justification ? `${prefix} — ${verdict.justification}` : prefix;
};

// Why a figure is absent, as the analysis itself explains it. Beats "no
// per-question X stored", which reads as a fault rather than a design decision.
//
// unavailable_metrics arrives in two shapes: the bulk scorer wrote a list of
// metric NAMES, meaning "this engine produces none of these"; the 0-4 rubric
// writes {metric, reason} objects naming the two figures it deliberately judges
// at paper level instead.
const unavailableReason = (unavailable, metric) => {
  const entry = (unavailable || []).find(
    (item) => item && typeof item === "object" && item.metric === metric);
  return entry?.reason || "not measured";
};

// ----------------------------------------------------------------- the pieces

// What this paper is, as the paper itself states it. The operator-typed values
// fill in where the cover is silent, and are labelled when they do: "we read
// this off the paper" and "somebody typed this at upload" are different claims,
// and the two disagree often enough that presenting them identically would hide
// the disagreement.
export const IdentityStrip = ({ analysis, paper }) => {
  const meta = analysis?.paper_metadata || {};
  const band = analysis?.overall_metrics?.band || null;

  const courseCode = meta.course_no || meta.course_id || paper?.course_id;
  const courseName = meta.course_title || meta.course_name || paper?.course_name;
  const examName = meta.exam_name || paper?.exam_type;
  const duration = meta.total_duration_minutes || paper?.total_duration_minutes;

  const facts = [
    meta.exam_set,
    meta.nature_of_exam || paper?.exam_format,
    meta.date_of_exam,
    duration ? `${duration} min` : null,
    meta.weightage ? `weightage ${meta.weightage}` : null,
    meta.number_of_pages ? `${meta.number_of_pages} pages` : null,
  ].filter(Boolean);

  return (
    <div className="qpe-identity">
      <div className="qpe-identity-main">
        {(meta.institution || meta.division) && (
          <p className="qpe-institution">
            {[meta.institution, meta.division].filter(Boolean).join(" · ")}
          </p>
        )}
        <h2>
          {courseCode}
          {courseName ? ` — ${courseName}` : ""}
        </h2>
        <p className="qpe-identity-facts">
          {examName}
          {facts.length > 0 ? ` · ${facts.join(" · ")}` : ""}
        </p>
      </div>
      {band?.id && (
        <div className="qpe-band-chip" title={band.decided_by || ""}>
          <span className="qpe-band-label">Exam category</span>
          <strong>{band.name || band.id}</strong>
          {Array.isArray(band.expected_coverage_pct) && (
            <span className="qpe-band-expect">
              expects {band.expected_coverage_pct[0]}–{band.expected_coverage_pct[1]}% of
              outcomes
            </span>
          )}
          {band.confidence && (
            <span className={`qpe-band-conf is-${band.confidence}`}>
              {band.confidence} confidence
            </span>
          )}
        </div>
      )}
    </div>
  );
};

// The single number a reviewer glances at first, and everything needed to read
// it honestly: what it is out of, how many questions it covers, and whether any
// criterion failed to score - which deflates it for a reason that is not about
// the paper.
export const HeadlineVerdict = ({ analysis }) => {
  const metrics = analysis?.overall_metrics || {};
  const questions = analysis?.questions_analysis || [];
  const unavailable = metrics.unavailable_metrics || [];
  const paperCriteria = analysis?.paper_level_analysis?.criteria || null;
  const time = metrics.time_analysis || null;
  const difficulty = metrics.weighted_difficulty;
  const quality = metrics.average_quality_score;
  const score = analysis?.final_weighted_score;

  // Criteria the model was asked to score and returned nothing usable for.
  // Counted across every question because one such criterion anywhere means the
  // paper's percentage is understated - and it looks exactly like a real
  // weakness, which is the whole reason to say so next to the number.
  const unscoredTotal = questions.reduce(
    (sum, question) => sum + criteriaSummary(question).unscored, 0);
  const paperUnscored = (analysis?.paper_level_analysis?.unscored || []).length;

  return (
    <>
      <div className="qpe-headline">
        <div className={`qpe-ring is-${score == null ? "unknown" : bandFor(score / 100)}`}>
          <span className="qpe-ring-value">
            {score != null ? score.toFixed(1) : "—"}
          </span>
          <span className="qpe-ring-unit">/100</span>
          {analysis?.final_rating && (
            <span className="qpe-ring-rating">{analysis.final_rating}</span>
          )}
        </div>
        <div className="qpe-tiles">
          <Tile
            icon={faScaleBalanced}
            label="Avg question quality"
            value={quality != null ? Number(quality).toFixed(1) : "—"}
            unit={quality != null ? "/10" : ""}
            note={`${metrics.analyzed_questions ?? questions.length} of ${
              metrics.total_questions ?? questions.length
            } questions analysed`}
            band={quality == null ? "" : bandFor(Number(quality) / 10)}
          />
          {/* Difficulty and workload, from whichever source the paper has.
              The older path derived a per-question difficulty number by
              weighting Bloom's levels. The 0-4 rubric refuses to: its guidance
              says difficulty and cognitive level are distinct and neither
              implies the other, so it judges both for the paper as a whole with
              a scored criterion and a justification. That is a better answer
              than a number, and far better than the "not measured" this used to
              show over a paper the rubric had explicitly assessed on exactly
              this. */}
          <Tile
            icon={faBrain}
            label="Difficulty"
            value={
              difficulty != null
                ? Number(difficulty).toFixed(2)
                : paperCriterionText(paperCriteria, "difficulty_appropriateness")
            }
            unit={difficulty != null ? "/6" : ""}
            textual={difficulty == null}
            band={
              difficulty != null
                ? ""
                : paperCriterionBand(paperCriteria, "difficulty_appropriateness")
            }
            note={
              difficulty != null
                ? "Bloom's-weighted across questions"
                : paperCriterionNote(
                    paperCriteria,
                    "difficulty_appropriateness",
                    unavailableReason(unavailable, "weighted_difficulty"),
                  )
            }
          />
          <Tile
            icon={faClock}
            label="Workload"
            value={
              time
                ? String(time.estimated_time_minutes)
                : paperCriterionText(paperCriteria, "time_workload")
            }
            unit={time ? " min" : ""}
            textual={!time}
            band={time ? "" : paperCriterionBand(paperCriteria, "time_workload")}
            note={
              time
                ? `${time.allocated_time_minutes} min allowed · ${time.time_status} (${time.time_efficiency_percentage}%)`
                : paperCriterionNote(
                    paperCriteria,
                    "time_workload",
                    unavailableReason(unavailable, "time_analysis"),
                  )
            }
          />
          <Tile
            icon={faListCheck}
            label="Total marks"
            value={metrics.total_marks != null ? metrics.total_marks : "—"}
            note={
              metrics.incomplete_questions
                ? `${metrics.incomplete_questions} question(s) read incomplete`
                : "no incomplete questions"
            }
            band={metrics.incomplete_questions ? "fair" : ""}
          />
        </div>
      </div>

      {/* The caveat belongs here, next to the number it applies to. An unscored
          criterion still counts toward the denominator, so the percentage above
          is lower than the paper earned - and nothing else on the page would
          distinguish that from a genuine weakness. */}
      {(unscoredTotal > 0 || paperUnscored > 0) && (
        <p className="qpe-caveat">
          <FontAwesomeIcon icon={faTriangleExclamation} />{" "}
          <span>
            {unscoredTotal > 0 && (
              <>
                {unscoredTotal} question{" "}
                {unscoredTotal === 1 ? "criterion" : "criteria"} could not be scored
              </>
            )}
            {unscoredTotal > 0 && paperUnscored > 0 ? ", and " : ""}
            {paperUnscored > 0 && (
              <>
                {paperUnscored} paper-level{" "}
                {paperUnscored === 1 ? "criterion" : "criteria"} could not be scored
              </>
            )}
            . An unscored criterion still counts toward the maximum, so the score above
            is understated by that much. This is a scoring failure on those criteria,
            not a judgement about the paper.
          </span>
        </p>
      )}

    </>
  );
};

// The verdict, as the points it makes rather than as prose.
//
// The summary said the same things at ten times the length: three sentences to
// arrive at "skewed toward lower-order Understand questions", which the issues
// list says in one line. So the bullets lead, filling the row, and the prose is
// behind a line of its own - kept rather than deleted, because it is the paper
// judge's own overall verdict and the only place some of its reasoning is
// written down, but not worth the wall of text it cost by default.
export const PaperDigest = ({ analysis }) => {
  const summary = analysis?.overall_summary;
  const decidedBy = analysis?.overall_metrics?.band?.decided_by;
  const issues = Array.isArray(analysis?.top_issues) ? analysis.top_issues : [];
  const strengths = Array.isArray(analysis?.top_strengths) ? analysis.top_strengths : [];
  if (issues.length === 0 && strengths.length === 0 && !summary && !decidedBy) return null;
  return (
    <div className="qpe-digest">
      {(issues.length > 0 || strengths.length > 0) && (
        <div className="qpe-findings">
          {issues.length > 0 && (
            <div className="qpe-finding-card is-issues">
              <h4>
                <FontAwesomeIcon icon={faFlag} /> Top issues
              </h4>
              <ul>
                {issues.map((item, index) => <li key={index}>{item}</li>)}
              </ul>
            </div>
          )}
          {strengths.length > 0 && (
            <div className="qpe-finding-card is-strengths">
              <h4>
                <FontAwesomeIcon icon={faCheckCircle} /> Top strengths
              </h4>
              <ul>
                {strengths.map((item, index) => <li key={index}>{item}</li>)}
              </ul>
            </div>
          )}
        </div>
      )}
      {(summary || decidedBy) && (
        <div className="qpe-digest-notes">
          {summary && (
            <Disclosure summary="The verdict in full">
              <p className="qpe-summary">{summary}</p>
            </Disclosure>
          )}
          {decidedBy && (
            <Disclosure summary="Why this exam category?">
              <p>{decidedBy}</p>
            </Disclosure>
          )}
        </div>
      )}
    </div>
  );
};

// The paper-level rubric, one row per criterion. Rows rather than cards: seven
// criteria as cards is a grid to be scanned twice, while seven rows with the
// score in a fixed column is a shape - it shows where the paper is weak without
// reading a word of the justifications, which are there for the ones worth
// stopping on.
export const PaperRubricSpine = ({ analysis }) => {
  const criteria = Object.entries(analysis?.paper_level_analysis?.criteria || {});
  const total = analysis?.paper_total;
  const max = analysis?.paper_max;
  if (criteria.length === 0) return null;
  return (
    <div className="qpe-section">
      <div className="qpe-section-head">
        <h3>
          <FontAwesomeIcon icon={faScaleBalanced} /> The paper as a whole
        </h3>
        {max ? (
          <span className={`qpe-section-score is-${bandFor(total / max)}`}>
            {total}
            <small>/{max}</small>
          </span>
        ) : null}
      </div>
      <ul className="qpe-spine">
        {criteria.map(([name, verdict]) => {
          const score = parseScore(verdict);
          const skipped = Boolean(verdict?.skipped);
          // Asked and unanswerable, which is neither a score nor a skip. It
          // still counts toward the maximum, so it is called out in warning
          // tone rather than greyed out like a skip.
          const failed = !skipped && !score;
          const band = skipped ? "skipped" : failed ? "failed" : bandFor(score.ratio);
          const issues = Array.isArray(verdict?.issues) ? verdict.issues : [];
          const body = skipped
            ? verdict?.reason || "Not assessed."
            : failed
              ? "The model returned no usable score for this criterion on this run. "
                + "It still counts toward the maximum, so the paper's total is "
                + "understated - this is not a judgement about the paper."
              : verdict?.justification || "No justification recorded.";
          return (
            <li className={`qpe-spine-row is-${band}`} key={name}>
              <div className="qpe-spine-top">
                <span className="qpe-spine-name">
                  {verdict?.name || criterionLabel(name)}
                </span>
                <span className="qpe-spine-bar" aria-hidden="true">
                  {score && <i style={{ width: `${score.ratio * 100}%` }} />}
                </span>
                <span className="qpe-spine-score">
                  {skipped ? "not scored" : failed ? "unscored" : scoreText(verdict, score)}
                  {verdict?.label && !skipped && !failed && (
                    <em>{verdict.label}</em>
                  )}
                </span>
              </div>
              <Disclosure
                summary={skipped ? "why it was not scored" : failed ? "what happened" : "reasoning"}
                className="qpe-spine-why"
              >
                <p>{body}</p>
                {issues.length > 0 && (
                  <ul className="qpe-issue-list">
                    {issues.map((issue, index) => <li key={index}>{issue}</li>)}
                  </ul>
                )}
              </Disclosure>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

// How much of the course this paper reaches, against how much a paper of its
// kind is expected to. Rendered even when it cannot be measured, because "no
// handout was uploaded, so the outcomes are unknown" is information and a blank
// space is not.
export const CoverageBlock = ({ analysis }) => {
  const coverage = analysis?.overall_metrics?.coverage;
  const band = analysis?.overall_metrics?.band || null;
  const available = analysis?.overall_metrics?.outcomes_available;
  // Nothing to say at all on a paper scored before coverage existed.
  if (!coverage && available === undefined && !band?.id) return null;

  const verdictBand = coverage?.verdict === "on target"
    ? "good"
    : coverage?.verdict === "below expected"
      ? "poor"
      : coverage?.verdict === "above expected"
        ? "fair"
        : "unknown";
  const uncovered = Array.isArray(coverage?.uncovered) ? coverage.uncovered : [];

  return (
    <div className="qpe-section">
      <div className="qpe-section-head">
        <h3>Coverage of course outcomes</h3>
      </div>
      {coverage ? (
        <>
          <div className={`qpe-coverage is-${verdictBand}`}>
            <span className="qpe-coverage-figure">
              {coverage.outcomes_covered}
              <small>/{coverage.outcomes_total}</small>
            </span>
            <div className="qpe-coverage-detail">
              <span className="qpe-coverage-pct">{coverage.coverage_pct}% of outcomes</span>
              {coverage.verdict ? (
                <span className="qpe-coverage-verdict">
                  {coverage.verdict}
                  {coverage.expected_pct
                    ? ` — its category expects ${coverage.expected_pct[0]}–${coverage.expected_pct[1]}%`
                    : ""}
                  {coverage.gap_pct
                    ? `, a gap of ${coverage.gap_pct} percentage points`
                    : ""}
                  .
                </span>
              ) : (
                <span className="qpe-coverage-verdict">
                  No exam category was assigned, so there is no expected range to
                  compare against — this is breadth alone.
                </span>
              )}
            </div>
          </div>
          {/* A strict cutoff, deliberately. The verdict compares the figure
              against the category's range with no tolerance buffer, so a paper
              at 44.9% against a 45-60% range reads "below expected" the same as
              one at 20%. The gap is printed above so the two are told apart by
              the number rather than by the label. */}
          {uncovered.length > 0 && (
            <Disclosure summary={`${uncovered.length} outcome(s) this paper does not assess`}>
              <ol className="qpe-uncovered">
                {uncovered.map((text, index) => <li key={index}>{text}</li>)}
              </ol>
            </Disclosure>
          )}
        </>
      ) : (
        <p className="qpe-empty">
          Not measured. No course handout has been uploaded for this course, so its
          learning outcomes are unknown and there is nothing to measure coverage
          against. Two rubric criteria — one per question, one for the paper — are
          skipped for the same reason, which is why this paper is scored out of a
          smaller maximum rather than marked down.
        </p>
      )}
    </div>
  );
};

// The spread of what the paper asks: Bloom's level, question type, topic. Counts
// as chips and bars rather than charts, because the numbers are small integers
// and a chart would imply precision they do not have.
export const DistributionPanel = ({ analysis }) => {
  const metrics = analysis?.overall_metrics || {};
  const aggregates = metrics.aggregates || {};
  const unavailable = metrics.unavailable_metrics || [];
  const questions = analysis?.questions_analysis || [];

  // Prefer the stored level->count map. Fall back to counting the questions
  // themselves, which is what a paper scored before that key existed has - the
  // question rows carry the Bloom's NAME, so the name is mapped to its level.
  let blooms = metrics.blooms_distribution || null;
  if (!blooms) {
    const derived = {};
    questions.forEach((question) => {
      const level = question.blooms_level ?? bloomNumber(question.bloom_name);
      if (level) derived[level] = (derived[level] || 0) + 1;
    });
    blooms = Object.keys(derived).length > 0 ? derived : null;
  }
  const bloomTotal = blooms
    ? Object.values(blooms).reduce((sum, n) => sum + (Number(n) || 0), 0)
    : 0;

  return (
    <div className="qpe-section">
      <div className="qpe-section-head">
        <h3>What the paper asks</h3>
      </div>
      <div className="qpe-distribution">
        <div className="qpe-bloom-block">
          <h4>Bloom's level</h4>
          {blooms && bloomTotal > 0 ? (
            <ul className="qpe-bloom">
              {Object.keys(BLOOM_NAMES).map((level) => {
                const count = Number(blooms[level] ?? 0);
                return (
                  <li key={level} className={count ? "" : "is-empty"}>
                    <span className="qpe-bloom-name">
                      <b>L{level}</b> {BLOOM_NAMES[level]}
                    </span>
                    <span className="qpe-bloom-bar">
                      <i style={{ width: `${(count / bloomTotal) * 100}%` }} />
                    </span>
                    <span className="qpe-bloom-count">{count}</span>
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="qpe-empty">
              No Bloom's distribution stored for this paper
              {/* Only the bulk scorer's shape means "this engine produces none of
                  these". The 0-4 rubric's unavailable_metrics names difficulty
                  and time, and Bloom's is not one of them - so this note must
                  not fire on it. */}
              {unavailable.some((item) => typeof item === "string")
                ? " — bulk scoring does not produce one"
                : ""}
              .
            </p>
          )}
        </div>
        <div className="qpe-chip-blocks">
          <Chips counts={aggregates.type_counts} title="Question type" />
          <Chips counts={aggregates.topic_counts} title="Topics assessed" />
          {!aggregates.type_counts && !aggregates.topic_counts && (
            <p className="qpe-empty">
              No question types or topics stored — the engine that scored this paper
              does not report them.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

// Where the paper is consistently strong or weak, without opening every question
// to find out.
export const CriterionMeans = ({ analysis }) => {
  const questions = analysis?.questions_analysis || [];
  const criteria = aggregateRubric(questions);
  if (criteria.length === 0) return null;
  const ranked = [...criteria].sort((a, b) => a.ratio - b.ratio);
  const weakest = ranked.filter((c) => c.ratio < 0.999).slice(0, 2);
  const strongest = ranked.filter((c) => c.ratio >= 0.999).slice(-2).reverse();
  return (
    <div className="qpe-section">
      <div className="qpe-section-head">
        <h3>Question rubric, averaged</h3>
        <span className="qpe-section-note">
          across {questions.length} analysed question{questions.length === 1 ? "" : "s"}
        </span>
      </div>
      <ul className="qpe-agg">
        {criteria.map((criterion) => (
          <li key={criterion.name} className={`is-${bandFor(criterion.ratio)}`}>
            <span className="qpe-agg-name">
              {criterion.label || criterionLabel(criterion.name)}
            </span>
            <span className="qpe-agg-bar">
              <i style={{ width: `${criterion.ratio * 100}%` }} />
            </span>
            <span className="qpe-agg-value">
              {criterion.mean.toFixed(1)}
              <small>/{criterion.max}</small>
            </span>
          </li>
        ))}
      </ul>
      {(strongest.length > 0 || weakest.length > 0) && (
        <p className="qpe-agg-read">
          {strongest.length > 0 && (
            <>
              <strong>Holds up on:</strong>{" "}
              {strongest.map((c) => c.label || criterionLabel(c.name)).join(", ")} — full
              marks on every question.{" "}
            </>
          )}
          {weakest.length > 0 && (
            <>
              <strong>Weakest on:</strong>{" "}
              {weakest
                .map((c) => `${c.label || criterionLabel(c.name)} (${c.mean.toFixed(1)}/${c.max})`)
                .join(", ")}
              .
            </>
          )}
        </p>
      )}
    </div>
  );
};

// Papers that divide into parts give each part its own duration, weightage and
// instructions, and each part restarts question numbering at 1. Rendered as tabs
// rather than merged into the paper-wide metadata, so one part's cover never
// speaks for the whole paper.
export const PartsPanel = ({ analysis }) => {
  const fromMetrics = analysis?.overall_metrics?.parts;
  const parts = (Array.isArray(fromMetrics) && fromMetrics.length > 0)
    ? fromMetrics
    : (analysis?.paper_parts || []);
  const [active, setActive] = useState(0);
  if (!parts || parts.length === 0) return null;
  const part = parts[Math.min(active, parts.length - 1)] || {};
  // Stored as text on paper_parts and as a list in overall_metrics.parts,
  // because one is a column and the other is the walk's own shape.
  const instructions = Array.isArray(part.instructions)
    ? part.instructions
    : String(part.instructions || "").split("\n").filter(Boolean);

  return (
    <div className="qpe-section">
      <div className="qpe-section-head">
        <h3>Parts</h3>
        <span className="qpe-section-note">
          question numbering restarts inside each part
        </span>
      </div>
      <div className="qpe-tabs">
        {parts.map((entry, index) => (
          <button
            type="button"
            key={entry.label || index}
            className={`qpe-tab${index === active ? " is-active" : ""}`}
            onClick={() => setActive(index)}
          >
            {entry.label || `Part ${index + 1}`}
          </button>
        ))}
      </div>
      <div className="qpe-part">
        {part.title && <p className="qpe-part-title">{part.title}</p>}
        <p className="qpe-part-facts">
          {[
            part.duration,
            part.weightage ? `weightage ${part.weightage}` : null,
            part.number_of_questions ? `${part.number_of_questions} questions declared` : null,
            part.questions_captured != null ? `${part.questions_captured} read` : null,
            part.marks_captured != null ? `${part.marks_captured} marks read` : null,
            Array.isArray(part.pages) && part.pages.length
              ? `pages ${part.pages.join(", ")}`
              : null,
          ].filter(Boolean).join(" · ") || "No part-level metadata printed."}
        </p>
        {instructions.length > 0 && (
          <Disclosure summary={`${instructions.length} printed instruction(s)`}>
            <ul className="qpe-instructions">
              {instructions.map((line, index) => <li key={index}>{line}</li>)}
            </ul>
          </Disclosure>
        )}
        {Array.isArray(part.marks_table) && part.marks_table.length > 0 && (
          <Disclosure summary="Printed marks table">
            <MarksTable rows={part.marks_table} />
          </Disclosure>
        )}
      </div>
    </div>
  );
};

const MarksTable = ({ rows }) => (
  <div className="qpe-table-scroll">
    <table className="qpe-marks-table">
      <thead>
        <tr>
          <th>Question</th>
          <th>Marks</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row, index) => (
          <tr key={index}>
            <td>{row.question}</td>
            <td>{row.marks}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

// What the paper printed and how it was read.
//
// Not on a paper that read cleanly: it is a record of the machinery, not a
// judgement, and a reviewer reading a scored paper has the document itself
// above. The evaluation notes it used to show are byte-identical to the
// recommendations already rendered in the verdict block, and the printed
// instructions are on the first page of the PDF.
//
// Reachable from ONE place now: the paper that could not be evaluated, where
// the page-by-page trail is the only thing on the screen that can say why - the
// walk records it precisely so that question has an answer.
export const PaperSourceMeta = ({ analysis }) => {
  const meta = analysis?.paper_metadata || {};
  const events = analysis?.page_events;
  const notes = Array.isArray(analysis?.notes) ? analysis.notes : [];
  const instructions = String(meta.general_instructions || "")
    .split("\n").filter(Boolean);
  const marksTable = Array.isArray(meta.marks_table) ? meta.marks_table : [];
  const hasAny = instructions.length > 0 || marksTable.length > 0
    || (events && events.length > 0) || notes.length > 0;
  if (!hasAny) return null;

  return (
    <div className="qpe-section qpe-source">
      <div className="qpe-section-head">
        <h3>The paper as printed, and how it was read</h3>
      </div>
      {notes.length > 0 && (
        <Disclosure summary={`${notes.length} note(s) from the evaluation`}>
          <ul className="qpe-instructions">
            {notes.map((line, index) => <li key={index}>{line}</li>)}
          </ul>
        </Disclosure>
      )}
      {instructions.length > 0 && (
        <Disclosure summary={`General instructions (${instructions.length} line(s))`}>
          <ul className="qpe-instructions">
            {instructions.map((line, index) => <li key={index}>{line}</li>)}
          </ul>
        </Disclosure>
      )}
      {marksTable.length > 0 && (
        <Disclosure summary="Printed marks table">
          <MarksTable rows={marksTable} />
        </Disclosure>
      )}
      {/* The per-page trail. This is the answer to "why is that question
          incomplete" and "why did this paper come back with nothing", and it is
          the only place either question can be answered - which is why the walk
          records it rather than printing it to a log nobody keeps. */}
      {events && events.length > 0 && (
        <Disclosure summary={`Page-by-page read trail (${events.length} page(s))`}>
          <ol className="qpe-trail">
            {events.map((entry, index) => (
              <li key={index}>
                <b>p{entry.page ?? index + 1}</b>
                <span>
                  {(Array.isArray(entry.events) ? entry.events : [String(entry)])
                    .join("; ") || "nothing reported"}
                </span>
              </li>
            ))}
          </ol>
        </Disclosure>
      )}
    </div>
  );
};

// A paper the walk could not read. Distinct from a bad paper: every figure is
// zero or absent, and a page that renders those as measurements says "0%, the
// worst paper we have" about a document nobody managed to open.
export const NotEvaluated = ({ analysis }) => {
  const metrics = analysis?.overall_metrics || {};
  const events = analysis?.page_events;
  const failedPages = (events || []).filter((entry) =>
    (Array.isArray(entry.events) ? entry.events : []).some(
      (line) => typeof line === "string" && line.startsWith("!!")));
  return (
    <div className="qpe-not-evaluated">
      <h3>
        <FontAwesomeIcon icon={faExclamationTriangle} /> This paper could not be
        evaluated
      </h3>
      <p>
        The analysis completed but found no questions to score
        {metrics.total_questions === 0 ? "" : " that could be stored"}, so there is
        nothing to review. It is not a paper that scored badly — nothing was
        measured.
      </p>
      {analysis?.error_message && (
        <p className="qpe-error-line">{analysis.error_message}</p>
      )}
      {failedPages.length > 0 && (
        <p>
          {failedPages.length} page(s) failed while being read. The trail below names
          each one.
        </p>
      )}
      <PaperSourceMeta analysis={analysis} />
    </div>
  );
};

// ------------------------------------------------------------------ the screen

const QpEvaluation = ({ analysis, paper }) => {
  if (!analysis) return null;
  const questions = analysis.questions_analysis || [];
  const metrics = analysis.overall_metrics || {};
  // A paper with no scored questions at all. Read from the questions actually
  // stored rather than from the count the analysis reports, because the two
  // disagree exactly when something went wrong.
  const evaluated = questions.length > 0
    || (metrics.total_questions ?? 0) > 0
    || analysis.final_weighted_score != null;

  if (!evaluated) {
    return (
      <div className="review-card qpe">
        <IdentityStrip analysis={analysis} paper={paper} />
        <NotEvaluated analysis={analysis} />
      </div>
    );
  }

  return (
    <div className="review-card qpe">
      <IdentityStrip analysis={analysis} paper={paper} />
      <HeadlineVerdict analysis={analysis} />
      <PaperDigest analysis={analysis} />
      <PaperRubricSpine analysis={analysis} />
      <CoverageBlock analysis={analysis} />
      <DistributionPanel analysis={analysis} />
      <CriterionMeans analysis={analysis} />
      <PartsPanel analysis={analysis} />
      {(analysis.final_recommendation
        || (analysis.recommendations || []).length > 0) && (
        <div className="qpe-section qpe-verdict">
          {analysis.final_recommendation && (
            <p>
              <FontAwesomeIcon icon={faLightbulb} /> <strong>Verdict:</strong>{" "}
              {analysis.final_recommendation}
            </p>
          )}
          {(analysis.recommendations || []).length > 0 && (
            <ul>
              {analysis.recommendations.map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default QpEvaluation;
